// src/component-files/KanbanBoard.jsx
import React, { useState, useEffect } from 'react';
import { useDrop, useDrag } from 'react-dnd';
import { Box, Typography, Paper, FormControl, MenuItem, Select } from '@mui/material';
import TaskButton from '../TaskButtons/TaskButton'; // Adjust path as needed

/***** COLUMN CONFIGS *****/
export const columnConfigs = {
    'K12 Nutrition Needs': [
        { id: 'column-1', title: 'Assessment', color: 'rgba(200, 230, 201, 0.1)' },
        { id: 'column-2', title: 'Planning', color: 'rgba(129, 199, 132, 0.1)' },
        { id: 'column-3', title: 'Implementation', color: 'rgba(56, 142, 60, 0.1)' },
        { id: 'column-4', title: 'Evaluation', color: 'rgba(27, 94, 32, 0.1)' }
    ],
    'USDA Food Data Central': [
        { id: 'column-1', title: 'Raw Data', color: 'rgba(255, 205, 210, 0.1)' },
        { id: 'column-2', title: 'Processed Data', color: 'rgba(239, 154, 154, 0.1)' },
        { id: 'column-3', title: 'Analysis', color: 'rgba(229, 115, 115, 0.1)' },
        { id: 'column-4', title: 'Report', color: 'rgba(211, 47, 47, 0.1)' }
    ],
    'USDA Food Buying Guide': [
        { id: 'column-1', title: 'Needs Assessment', color: 'rgba(197, 202, 233, 0.1)' },
        { id: 'column-2', title: 'Vendor Research', color: 'rgba(159, 168, 218, 0.1)' },
        { id: 'column-3', title: 'Cost Analysis', color: 'rgba(121, 134, 203, 0.1)' },
        { id: 'column-4', title: 'Procurement', color: 'rgba(92, 107, 192, 0.1)' }
    ]
};

/***** SAMPLE TASKS (OPTIONAL) *****/
const initialTasks = {
    'task-1': { id: 'task-1', content: 'Sample Task 1' },
    'task-2': { id: 'task-2', content: 'Sample Task 2' },
};

const KanbanBoard = () => {
    // Default to K12
    const [selectedCategory, setSelectedCategory] = useState('K12 Nutrition Needs');

    // global tasks dictionary
    const [tasks, setTasks] = useState(initialTasks);

    // columns state: each column has an array of taskIds
    const [columns, setColumns] = useState(
        columnConfigs[selectedCategory].reduce((acc, col) => {
            acc[col.id] = { ...col, taskIds: [] };
            return acc;
        }, {})
    );

    // Regenerate columns whenever we change categories
    useEffect(() => {
        setColumns(
            columnConfigs[selectedCategory].reduce((acc, col) => {
                acc[col.id] = { ...col, taskIds: [] };
                return acc;
            }, {})
        );
    }, [selectedCategory]);

    /***** CORE DROP HANDLER *****/
    const handleDrop = (item, targetColumnId) => {
        const taskId = item.id; // if this is an existing item

        // If the item has an id, it's an existing task => move it
        if (taskId) {
            setColumns(prev => {
                // clone columns
                const updated = { ...prev };

                // remove taskId from whichever column it's in
                for (const colId in updated) {
                    updated[colId] = {
                        ...updated[colId],
                        taskIds: updated[colId].taskIds.filter(id => id !== taskId)
                    };
                }

                // now add it to the top of the target column
                if (!updated[targetColumnId].taskIds.includes(taskId)) {
                    updated[targetColumnId] = {
                        ...updated[targetColumnId],
                        taskIds: [taskId, ...updated[targetColumnId].taskIds]
                    };
                }

                return updated;
            });
        } else {
            // if item.id doesn't exist, it's a brand-new item
            const newId = `task-${Date.now()}`;
            const newTask = { id: newId, content: item.content };
            setTasks(prev => ({ ...prev, [newId]: newTask }));

            setColumns(prev => ({
                ...prev,
                [targetColumnId]: {
                    ...prev[targetColumnId],
                    taskIds: [newId, ...prev[targetColumnId].taskIds]
                }
            }));
        }
    };

    return (
        <>
            <FormControl fullWidth sx={{ mb: 2 }}>
                <Select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    displayEmpty
                >
                    {Object.keys(columnConfigs).map(cat => (
                        <MenuItem key={cat} value={cat}>
                            {cat}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Box display="flex" gap={2} p={2} overflow="auto">
                {Object.values(columns).map((col) => (
                    <DroppableColumn
                        key={col.id}
                        column={col}
                        tasks={tasks}
                        onDrop={handleDrop}
                    />
                ))}
            </Box>
        </>
    );
};

/***** COLUMN COMPONENT *****/
const DroppableColumn = ({ column, tasks, onDrop }) => {
    const [{ isOver }, dropRef] = useDrop({
        accept: 'task',
        drop: (item, monitor) => {
            if (monitor.isOver({ shallow: true })) {
                onDrop(item, column.id);
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver({ shallow: true }),
        }),
    });

    return (
        <Paper
            ref={dropRef}
            sx={{
                p: 2,
                width: '100%',
                minHeight: 640, // 60% taller than 400
                bgcolor: isOver ? 'rgba(0,0,0,0.1)' : column.color,
                borderRadius: 2,
                boxShadow: 3,
            }}
        >
            <Typography variant="h6" align="center" sx={{ mb: 2, fontWeight: 'bold' }}>
                {column.title}
            </Typography>

            {/* Show tasks in this column */}
            {column.taskIds.map(taskId => {
                const t = tasks[taskId];
                if (!t) return null; // skip if missing

                return <DraggableTask key={taskId} task={t} />;
            })}
        </Paper>
    );
};

/***** DRAGGABLE TASK COMPONENT *****/
const DraggableTask = ({ task }) => {
    const [{ isDragging }, dragRef] = useDrag({
        type: 'task',
        item: { ...task }, // includes `id` and `content`
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging(),
        }),
    });

    return (
        <div
            ref={dragRef}
            style={{
                opacity: isDragging ? 0.5 : 1,
                marginBottom: 8,
                cursor: 'grab',
            }}
        >
            <TaskButton content={task.content} />
        </div>
    );
};

export default KanbanBoard;
